import React, { useState, useRef } from "react";
import { Typography, Box, Modal, Button, CircularProgress, Grid2 } from "@mui/material";
import axios from "axios";
import SuccessModal from "../../../../utils/SuccessModal";
import ErrorModal from "../../../../utils/ErrorModal";
import { handleExportPDF } from "../../../../utils/exportToPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const Step4 = ({ rfq, items, approvers, selectVendors, setStep }) => {
  const pdfRef = useRef();

  const [openConfirm, setOpenConfirm] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const storedUser = JSON.parse(localStorage.getItem("user")) || {};

  const safeItems = Array.isArray(items) ? items : [];
  const currencyArray = Array.isArray(rfq?.currencies) ? rfq.currencies : [];

  const exchangeRateMap = Object.fromEntries(
    currencyArray
      .filter((c) => c?.name)
      .map((c) => [c.name, parseFloat(c.rate) || 0])
  );

  const grandTotal = safeItems
    .reduce((sum, item) => sum + parseFloat(item?.total || 0), 0)
    .toFixed(2);

  const handleConfirmSubmit = async () => {
    setBtnLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/logistic-draft`,
        {
          ...rfq,
          currencies: currencyArray,
          status: "ACCEPTING_BIDS",
          username: storedUser.username,
          purchaseHead: approvers.purchaseHead,
          awardee1: approvers.awardee1,
          awardee2: approvers.awardee2,
          vendors: selectVendors,
        },
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        }
      );

      setBtnLoading(false);
      setOpenConfirm(false);
      setSuccessOpen(true);
    } catch (error) {
      console.log(error);
      setBtnLoading(false);
      setOpenConfirm(false);
      setErrorModal(true);
      setErrorMsg(error.response?.data || "Submission failed.");
    }
  };

  const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const [date, time] = dateString.split("T");
  if (!date) return "";
  const [year, month, day] = date.split("-");
  const [hours, minutes] = time?.split(":") || [];
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

  return (
    <div style={{ position: "relative" }}>
      {exporting && (
        <div className="uploading-div">
          Exporting PDF... This may take a while. <CircularProgress color="#fff" />
        </div>
      )}

      {btnLoading && (
        <div className="loading-div uploading-div">
          Submitting Logistics RFQ... <CircularProgress color="#fff" />
        </div>
      )}

      <h2>Preview Logistics RFQ</h2>
      <div style={{ display: "flex", justifyContent: "end", gap: 10 }}>
        <Button className="btn-outlined" onClick={() => setStep((prev) => prev - 1)}>
          Back
        </Button>
        <Button className="btn-contained" onClick={() => setOpenConfirm(true)}>
          Submit
        </Button>
        <Button
          className="btn-contained"
          sx={{ width: "200px" }}
          onClick={async () => {
            try {
              setExporting(true);
              await handleExportPDF(pdfRef, rfq?.logisticDraftId);
            } catch (error) {
              console.log(error);
            } finally {
              setExporting(false);
            }
          }}
        >
          Export PDF
        </Button>
      </div>

      <div ref={pdfRef}>
        <Box className="dashboard-container" px={10}>
          <Typography className="form-title">Logistic RFQ Details</Typography>
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderTop: "1px solid #000" }}>
              Logistic Draft ID
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000", borderRight: "1px solid #000", fontWeight: "bold" }}
            >
              {rfq?.logisticDraftId || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Mode of Consignment</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.modeOfConsignment || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Consignee</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.consignee || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Submission Date</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {formatDateTime(rfq?.submissionDate) || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Invoice No</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.invoiceNo || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Transit Time</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.transitTime || "-"} days
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">No of Pallets</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.noOfPallets || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Address</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.address || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Origin</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.origin || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Destination</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.destination || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Gross Weight</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.grossWeight || "-"} Kg
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Length</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.length || "-"} inches
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Breadth</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.breadth || "-" } inches
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Height</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.height || "-"} inches
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">Volume (CBM)</Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {rfq?.volumeCBM || "-"} CBM
            </Grid2>
          </Grid2>
        </Box>

        <h2>Currency Details</h2>
        <Box className="dashboard-container" px={10}>
          <table
            border={1}
            style={{
              borderCollapse: "collapse",
              width: "100%",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ padding: "20px" }}>Currency</th>
                <th>Exchange Rate</th>
              </tr>
            </thead>
            <tbody>
              {currencyArray.length === 0 ? (
                <tr>
                  <td style={{ padding: "20px", textAlign: "center" }} colSpan={2}>
                    No currencies selected
                  </td>
                </tr>
              ) : (
                currencyArray.map((c, i) => (
                  <tr key={i} style={{ textAlign: "center" }}>
                    <td style={{ padding: "20px" }}>{c.name}</td>
                    <td>{c.rate}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>

        <h2>Work Items Details</h2>
        <Box className="dashboard-container" px={10}>
          <table
            border={1}
            style={{
              borderCollapse: "collapse",
              width: "100%",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ padding: "20px" }}>Sr. No</th>
                <th>Particulars</th>
                <th>Unit</th>
                <th>Quantity</th>
                <th>Currency</th>
                <th>Exchange Rate</th>
                <th>Rate</th>
                <th>Total (in &#8377;)</th>
              </tr>
            </thead>
            <tbody>
              {safeItems.sort((a, b) => a.itemOrder - b.itemOrder).map((item, index) => (
                <tr key={index} style={{ textAlign: "center" }}>
                  <td style={{ padding: "20px" }}>{item?.srNo || index + 1}</td>
                  <td>{item?.particulars || "-"}</td>
                  <td>{item?.unit || "-"}</td>
                  <td>{item?.quantity || "-"}</td>
                  <td>{item?.currency || "-"}</td>
                  <td>{item?.exchangeRate ?? exchangeRateMap[item?.currency] ?? "-"}</td>
                  <td>{item?.newRate ?? "-"}</td>
                  <td>
                    {new Intl.NumberFormat("en-IN", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(item?.total || 0))}
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
                <td colSpan={7} style={{ textAlign: "right", paddingRight: "10px" }}>
                  Grand Total (in &#8377;):
                </td>
                <td style={{ width: "150px", textAlign: "center", padding: "20px" }}>
                  {new Intl.NumberFormat("en-IN", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography variant="h6">Approvers</Typography>
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderTop: "1px solid #000" }}>
              PURCHASE MANAGER
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000", borderRight: "1px solid #000" }}
            >
              {approvers?.purchaseHead || "-"}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              FINAL APPROVER 1
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {approvers?.awardee1 || "N/A"}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              FINAL APPROVER 2
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{ borderRight: "1px solid #000" }}>
              {approvers?.awardee2 || "-"}
            </Grid2>
          </Grid2>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography variant="h6">Vendors</Typography>
          <Box>{(selectVendors || []).join(", ") || "-"}</Box>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2, flexWrap: "wrap" }}>
            <Button className="btn-outlined" onClick={() => setStep((prev) => prev - 1)}>
              Back
            </Button>
            <Button className="btn-contained" onClick={() => setOpenConfirm(true)}>
              Submit
            </Button>
          </Box>
        </Box>
        
      </div>

      <Modal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Are you sure you want to submit details for {rfq?.logisticDraftId}?
          </Typography>
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button className="btn-outlined" onClick={() => setOpenConfirm(false)}>
              No
            </Button>
            <Button className="btn-contained" onClick={handleConfirmSubmit} disabled={btnLoading}>
              {btnLoading ? "Submitting..." : "Yes"}
            </Button>
          </Box>
        </Box>
      </Modal>
 
      <SuccessModal
        open={successOpen}
        handleClose={() => setSuccessOpen(false)}
        msg={"Logistics RFQ Submitted Successfully!"}
        nav={"/logistic-dashboard"}
      />

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default Step4;
